import _ from 'lodash'
import { FTProcess } from '@/models/FTProcess';
import { weightToPieces, shapeArea, initProcess, ProcessConfig, ConversionResults, ParsedVariables } from './index'
import { FTProcessRow } from '@/models/FTProcessRow';
import { ConversionDetails } from '@/models/ConversionDetails';

export function loadConfig({ processRow }: ParsedVariables): ProcessConfig {
  return {
    inputs: {
      goods: [
        {
          goodsType: '',
          goodsFilter: {
            form: ['sheet', 'die-cut-sheet']
          },
          multiple: false,
          params: [
            {
              name: 'Width',
              type: 'label',
              value: _.get(processRow, 'goodsObject.dimensions.width', ''),
              unit: 'mm',
              disabled: true
            },{
              name: 'Length',
              type: 'label',
              value: _.get(processRow, 'goodsObject.dimensions.length', ''),
              unit: 'mm',
              disabled: true
            },
            {
              name: 'Unit Weight',
              type: 'label',
              value: _.get(processRow, 'goodsObject.unitWeight', ''),
              unit: 'kg',
              disablePrint: true,
              disabled: true
            },
            {
              name: 'Input Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        }
      ]
    },
    outputs: {
      type: 'multiple',
      arguments: [
        {
          name: 'Shape',
          type: 'shape',
          value: 'shape',
          optionsArray: [ 'Round', 'Oval', 'Rectangle', 'Rectangle+Oval', 'Other'],
          disabled: false
        },
        {
          name: 'Area',
          type: 'dimension',
          value: 'area',
          unit: 'sq. mm',
          showIf: _.get(processRow, 'dimensions.shape') === 'Other',
          disabled: false
        },
        {
          name: 'Diameter',
          type: 'dimension',
          value: 'diameter',
          unit: 'mm',
          showIf: _.get(processRow, 'dimensions.shape') === 'Round',
          disabled: false
        },
        {
          name: 'Width',
          type: 'dimension',
          value: 'width',
          unit: 'mm',
          showIf: ['Oval', 'Rectangle', 'Rectangle+Oval'].includes(_.get(processRow, 'dimensions.shape')),
          disabled: false
        },
        {
          name: 'Length',
          type: 'dimension',
          value: 'length',
          unit: 'mm',
          showIf: ['Oval', 'Rectangle', 'Rectangle+Oval'].includes(_.get(processRow, 'dimensions.shape')),
          disabled: false
        },
        {
          name: 'Output Quantity',
          type: 'quantity',
          value: 'quantity',
          disabled: false
        }
      ]
    },
    tableHeaders: [
      {
        text: 'Nominal Speed',
        value: 'nominalSpeed'
      },
      {
        text: 'Piece(s) per stroke',
        value: 'pcsPerStroke'
      },
      {
        text: 'Planned Hours(s)',
        value: 'plannedHours'
      },
      {
        text: 'No of Pieces',
        value: 'numberOfPieces'
      },
      {
        text: 'Expected Output Waste',
        value: 'wastePercentage'
      },
      {
        text: 'Expected Output Waste Weight',
        value: 'waste'
      },
      {
        text: 'Expected Output Net Weight',
        value: 'netWeight'
      }
    ]
  }
}

export function convert(process: FTProcess) : ConversionResults {
  const conversionResult : ConversionResults = {
    inputs: [],
    outputs: [],
    details: new ConversionDetails(),
  };

  initProcess(process);

  let totalNoOfSheets = 0, piecesEstimate = 0, inputQuantity = 0, netWeight = 0, totalOutputUnitWeight = 0;
  conversionResult.details.totalQuantity = 0;
  conversionResult.outputs = process.outputs;
  
  _.map(process.inputs, (processInputRow: FTProcessRow) => {
    inputQuantity += processInputRow.quantity;
    const numberofSheets = weightToPieces(
      processInputRow.quantity, processInputRow.goodsObject.density,
      shapeArea({ shape: _.get(processInputRow, 'goodsObject.dimensions.shape'), params: processInputRow.goodsObject.dimensions })
    );
    totalNoOfSheets += numberofSheets;
  });
  _.forEach(process.outputs, (outputFTProcessRow: FTProcessRow) => {
    netWeight += outputFTProcessRow.quantity || 0;
    outputFTProcessRow.goodsObject.copyFrom(process.inputs[0].goodsObject);
    outputFTProcessRow.goodsObject.form = 'die-cut-sheet';
    if (outputFTProcessRow.dimensions) {
      outputFTProcessRow.goodsObject.dimensions = outputFTProcessRow.dimensions;
    }
    const dieShapeArea = shapeArea({ shape: outputFTProcessRow.dimensions.shape, params: outputFTProcessRow.dimensions });
    const dieShapeAreaInM2 = dieShapeArea / 1000000;
    outputFTProcessRow.goodsObject.unitWeight = (outputFTProcessRow.goodsObject.density * dieShapeAreaInM2) / 1000;
    const noOfPieces = weightToPieces(outputFTProcessRow.quantity, outputFTProcessRow.goodsObject.density, dieShapeArea);
    outputFTProcessRow.goodsObject.setPerKgRatio('plA', noOfPieces/outputFTProcessRow.quantity);
    piecesEstimate += noOfPieces;
    totalOutputUnitWeight += outputFTProcessRow.goodsObject.unitWeight;
  });
  
  conversionResult.details.netWeight = netWeight;
  conversionResult.details.waste = inputQuantity - netWeight;
  conversionResult.details.wastePercentage = (conversionResult.details.waste/inputQuantity) * 100;
  conversionResult.details.numberOfPieces = piecesEstimate;
  process.machineDetail.pcsPerStroke = totalNoOfSheets ? Math.floor(piecesEstimate / totalNoOfSheets) : 0;
  if (process.machineDetail.pcsPerStroke && totalOutputUnitWeight) {
      conversionResult.details.kgPerHour = 60 * totalOutputUnitWeight * process.machineDetail.mechanicalSpeed * process.machineDetail.pcsPerStroke;
      conversionResult.details.plannedHours = inputQuantity / conversionResult.details.kgPerHour;
  }
  conversionResult.details.pcsPerStroke = process.machineDetail.pcsPerStroke;

  return conversionResult;
}